import React, {useCallback, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';

import {useContentMgr} from 'coreHooks';
import {ECMSMode} from 'coreStore/data/configurations/types';
import type {ICpaConnectionOverride} from 'coreUtils/cms/contentHubConnection';
import {DEFAULT_CPA_HOST} from 'coreUtils/cms/contentHubConnection';
import {getCpaConnectionInfo, getLivePreviewProviderOptions} from 'coreUtils/livePreview';
import {getConfig} from 'coreUtils/utils';

import {clearCpaConnection, saveCpaConnection} from './api';
import {ICpaConnectionPreset, ICPAConnectionSectionProps} from './ICPAConnectionSection';
import messages from './messages';

import styles from './CPAConnectionSection.module.scss';

const PRESET_DEFAULT = '';
const PRESET_CUSTOM = 'custom';

type TForm = Required<ICpaConnectionOverride>;

const EMPTY_FORM: TForm = {host: '', spaceId: '', environmentId: '', token: '', insecure: false, cmsUrl: ''};

const toForm = (values: Omit<ICpaConnectionOverride, 'token'> | null): TForm => ({
	...EMPTY_FORM,
	...Object.fromEntries(Object.entries(values ?? {}).filter(([key]) => key in EMPTY_FORM)),
	token: '',
});

/**
 * DevToolkit section that points the CMS preview to another Content Hub (e.g. the development CMS, another
 * space or environment) for this browser session. The values go to the server
 * (`CPA_CONNECTION_ROUTE`), which validates them and keeps them in httpOnly cookies: the API key never comes back
 * to the browser.
 */
const CPAConnectionSection = ({onApplied}: ICPAConnectionSectionProps) => {
	const intl = useIntl();
	const contentMgr = useContentMgr();
	const params = getConfig()?.app?.cmsAPI?.parameters ?? {};
	const presets: ICpaConnectionPreset[] = params.cpaPresets ?? [];
	const [current] = useState(getCpaConnectionInfo);
	const defaults = useMemo(() => {
		const {cmsUrl} = getLivePreviewProviderOptions();

		return {
			host: DEFAULT_CPA_HOST,
			spaceId: params.spaceId,
			environmentId: params.environmentId,
			cmsUrl,
		};
	}, [params.spaceId, params.environmentId]);
	const [presetId, setPresetId] = useState<string>(current ? PRESET_CUSTOM : PRESET_DEFAULT);
	const [form, setForm] = useState<TForm>(() => toForm(current));
	const [errors, setErrors] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	const handlePreset = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
		const id = e.target.value;
		const preset = presets.find((p) => p.id === id);

		setPresetId(id);
		setErrors([]);

		if (id === PRESET_DEFAULT) {
			setForm(EMPTY_FORM);
		} else if (preset) {
			setForm({...toForm(preset), token: ''});
		}
	}, [presets]);

	const handleField = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const {name, value, type, checked} = e.target;
		const nextValue = type === 'checkbox' ? checked : value;

		setPresetId((id) => (id === PRESET_DEFAULT ? PRESET_CUSTOM : id));
		setForm((prev) => ({...prev, [name]: nextValue}));
	}, []);

	const reload = useCallback(() => {
		onApplied?.();
		window.location.reload();
	}, [onApplied]);

	const handleApply = useCallback(async () => {
		setIsSaving(true);

		if (presetId === PRESET_DEFAULT && !Object.values(form).some(Boolean)) {
			await clearCpaConnection();
			reload();

			return;
		}

		const invalid = await saveCpaConnection({
			...form,
			// The current API key can not be read back: keep it when the field stays empty
			...(!form.token && current?.hasToken ? {keepToken: true} : {}),
		} as ICpaConnectionOverride);

		if (invalid.length > 0) {
			setErrors(invalid);
			setIsSaving(false);

			return;
		}

		contentMgr.changeMode(ECMSMode.PREVIEW);
		reload();
	}, [presetId, form, current, contentMgr, reload]);

	const handleReset = useCallback(async () => {
		setIsSaving(true);
		await clearCpaConnection();
		reload();
	}, [reload]);

	const renderInput = (name: keyof TForm, placeholder?: string, type = 'text') => (
		<label className={styles['cpa-field']}>
			<span className={styles['cpa-field__label']}>{intl.formatMessage(messages[name])}</span>
			<input
				className={styles['cpa-field__input']}
				name={name}
				type={type}
				autoComplete="off"
				spellCheck={false}
				value={form[name] as string}
				placeholder={placeholder}
				aria-invalid={errors.includes(name)}
				onChange={handleField}
			/>
		</label>
	);

	return (
		<div className={styles['cpa-section']}>
			<div className={styles['cpa-section__heading']}>
				<span className={styles['cpa-section__title']}>{intl.formatMessage(messages.title)}</span>
				{current && <span className={styles['cpa-section__badge']}>{intl.formatMessage(messages.overrideBadge)}</span>}
			</div>
			<p className={styles['cpa-section__hint']}>{intl.formatMessage(messages.hint)}</p>

			<label className={styles['cpa-field']}>
				<span className={styles['cpa-field__label']}>{intl.formatMessage(messages.preset)}</span>
				<select className={styles['cpa-field__input']} value={presetId} onChange={handlePreset}>
					<option value={PRESET_DEFAULT}>{intl.formatMessage(messages.presetDefault)}</option>
					{presets.map((preset) => (
						<option key={preset.id} value={preset.id}>{preset.label}</option>
					))}
					<option value={PRESET_CUSTOM}>{intl.formatMessage(messages.presetCustom)}</option>
				</select>
			</label>

			{renderInput('host', defaults.host)}

			<div className={styles['cpa-section__row']}>
				{renderInput('spaceId', defaults.spaceId)}
				{renderInput('environmentId', defaults.environmentId)}
			</div>

			{renderInput('token', current?.hasToken ? intl.formatMessage(messages.tokenSet) : intl.formatMessage(messages.tokenHint), 'password')}
			{renderInput('cmsUrl', defaults.cmsUrl)}

			<label className={styles['cpa-checkbox']}>
				<input type="checkbox" name="insecure" checked={form.insecure} onChange={handleField} />
				<span>{intl.formatMessage(messages.insecure)}</span>
			</label>

			{errors.length > 0 && (
				<p className={styles['cpa-section__error']} role="alert">
					{intl.formatMessage(messages.error, {fields: errors.join(', ')})}
				</p>
			)}

			<div className={styles['cpa-section__actions']}>
				{current && (
					<button type="button" className={styles['cpa-button']} disabled={isSaving} onClick={handleReset}>
						{intl.formatMessage(messages.reset)}
					</button>
				)}
				<button type="button" className={styles['cpa-button']} disabled={isSaving} onClick={handleApply}>
					{intl.formatMessage(messages.apply)}
				</button>
			</div>
		</div>
	);
};

export default CPAConnectionSection;
